import { useCallback, useMemo } from 'react';
import cn from 'classnames';

import {
  IKnmPreview,
  ItAccreditationResponseOrgStatusDictionary,
} from 'ApiPackage/SearchModule/interfaces/IKnmPreview';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG } from 'CorePackage/MainModule/constants/dateFormat';
import { Checkbox } from 'UIPackage/MainModule/components/Checkbox';

import { parseErknmValue } from '../common/parseErknmValue';
import { KnmSubtitle } from './KnmSubtitle';
import styles from './styles.module.scss';
import { mspCategoryTitlesByMspCodes } from '../common/mspCategoryTitlesByMspCodes';
import { MspCodesEnum } from '../enums/MspCodesEnum';
import { KnmSubjectTypesEnum } from '../enums/KnmSubjectTypesEnum';
import { DecisionSubjectTypesEnum } from '../enums/DecisionSubjectTypesEnum';

interface IPmFaceProps {
  passportData?: IKnmPreview;
}

export const PmFace = ({ passportData }: IPmFaceProps) => {
  const mspCode =
    passportData?.pmErknm.subject &&
    (passportData?.pmErknm.subject.isIndividualEntrepreneur ||
      passportData?.pmErknm.subject.isLegalEntity ||
      passportData?.pmErknm.subject.isSmallEnterprise);

  const isSonko = passportData?.pmErknm.subject && passportData.pmErknm.subject.isSonko;

  const isMspCode =
    passportData?.pmErknm.subject.mspCode && passportData.pmErknm.subject.mspCode !== MspCodesEnum.NON_EXIST;

  const subjectType = passportData?.pmErknm?.subject?.subjectType;
  const country = passportData?.pmErknm?.subject?.country;
  const snils = passportData?.pmErknm?.subject?.snils;
  const document = passportData?.pmErknm?.subject?.document;
  const locationAddress = passportData?.pmErknm?.subject?.locationAddress;
  const workAddress = passportData?.pmErknm?.subject?.workAddress;
  const factualAddress = passportData?.pmErknm?.subject?.factualAddress;
  const rafpNza = passportData?.pmErknm?.subject?.rafpNza;
  const isPresentInItRegistry = passportData?.pmErknm?.subject?.isPresentInItRegistry;
  const itAccreditationResponse = passportData?.pmErknm?.subject?.itAccreditationResponse;

  const isUR = subjectType === DecisionSubjectTypesEnum.UR;
  const isIUR = subjectType === DecisionSubjectTypesEnum.IUR;
  const isIP = subjectType === DecisionSubjectTypesEnum.IP;
  const isFIUR = subjectType === DecisionSubjectTypesEnum.FIUR;
  const IFIZ = subjectType === DecisionSubjectTypesEnum.IFIZ;

  const organizationNameColText = useMemo(() => {
    switch (subjectType) {
      case KnmSubjectTypesEnum.FIZ:
      case KnmSubjectTypesEnum.IFIZ:
        return 'Фамилия, имя, отчество (при наличии)';
      case KnmSubjectTypesEnum.IUR:
        return 'Наименование';
      case KnmSubjectTypesEnum.FIUR:
        return 'Наименование организации';
      default:
        return 'Наименование проверяемого лица';
    }
  }, [subjectType]);

  const openErvkGovRu = useCallback((cosId: string) => {
    window.open(`https://ervk.gov.ru/objects/${cosId}`, '_blank');
  }, []);

  return (
    <div className={styles.ColumnContent}>
      <span className={styles.SectionTitle}>Сведения о контролируемом лице</span>
      {subjectType && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Тип контролируемого лица</div>
          <div className={styles.ColValue}>{subjectType}</div>
        </div>
      )}
      {passportData?.pmErknm.subject && passportData?.pmErknm.subject.inn && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Идентификационный номер налогоплательщика (ИНН)</div>
          <div className={styles.ColValue}>{passportData?.pmErknm.subject.inn}</div>
        </div>
      )}
      {passportData?.pmErknm.subject && passportData?.pmErknm.subject.ogrn && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Основной государственный регистрационный номер (ОГРН)</div>
          <div className={styles.ColValue}>{passportData?.pmErknm.subject.ogrn}</div>
        </div>
      )}
      {snils && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Страховой номер индивидуального лицевого счета (СНИЛС)</div>
          <div className={styles.ColValue}>{snils}</div>
        </div>
      )}
      {rafpNza && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Номер записи об аккредитации (НЗА)</div>
          <div className={styles.ColValue}>{rafpNza}</div>
        </div>
      )}
      {passportData?.pmErknm.subject && passportData?.pmErknm.subject.organizationName && (
        <div className={styles.Row}>
          <div className={styles.ColText}>{organizationNameColText}</div>
          <div className={styles.ColValue}>{passportData?.pmErknm.subject.organizationName}</div>
        </div>
      )}
      {(isUR || isIP || isFIUR) && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Наличие проверяемого лица в реестре МСП</div>
          <div className={styles.ColValue}>{isMspCode ? 'Да' : 'Нет'}</div>
        </div>
      )}
      {isMspCode && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Категория субъекта МСП</div>
          <div className={styles.ColValue}>
            {mspCategoryTitlesByMspCodes[passportData?.pmErknm.subject.mspCode as MspCodesEnum]}
          </div>
        </div>
      )}
      {country && isIUR && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Государство, в котором указанное юридическое лицо зарегистрировано</div>
          <div className={styles.ColValue}>{country}</div>
        </div>
      )}
      {country && IFIZ && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Гражданство (при наличии)</div>
          <div className={styles.ColValue}>{country}</div>
        </div>
      )}
      {mspCode && (
        <div className={styles.Row}>
          <div className={styles.ColText}>
            Сведения об отнесении контролируемого лица к субъектам малого и среднего бизнеса
          </div>
          <div className={styles.ColValue}>{mspCode}</div>
        </div>
      )}
      {document && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Сведения о документе, удостоверяющем личность</div>
          <div className={styles.ColValue}>{document}</div>
        </div>
      )}
      {locationAddress && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Адрес места нахождения</div>
          <div className={styles.ColValue}>{locationAddress}</div>
        </div>
      )}
      {workAddress && !isIP && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Место нахождения (осуществления деятельности) контролируемого лица</div>
          <div className={styles.ColValue}>{workAddress}</div>
        </div>
      )}
      {factualAddress && !isIP && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Место фактического осуществления деятельности</div>
          <div className={styles.ColValue}>{factualAddress}</div>
        </div>
      )}
      {typeof isSonko === 'boolean' && (isUR || isFIUR) && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Наличие проверяемого лица в реестре СОНКО</div>
          <div className={styles.ColValue}>{isSonko ? 'Да' : 'Нет'}</div>
        </div>
      )}
      {isPresentInItRegistry !== undefined && isUR && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Наличие в реестре аккредитованных IT-организаций</div>
          <div className={styles.ColValue}>{isPresentInItRegistry ? 'Да' : 'Нет'}</div>
        </div>
      )}
      {isPresentInItRegistry && itAccreditationResponse && (
        <>
          <div className={styles.Row}>
            <div className={styles.ColText}>Регистрационный номер</div>
            <div className={styles.ColValue}>{itAccreditationResponse.registryNumber}</div>
          </div>
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата включения</div>
            <div className={styles.ColValue}>
              {formatDate({
                date: itAccreditationResponse.dateDecisionAccreditation || '',
                dateFormat: DATE_FORMAT_LONG,
              })}
            </div>
          </div>
          <div className={styles.Row}>
            <div className={styles.ColText}>Статус</div>
            <div className={styles.ColValue}>
              {ItAccreditationResponseOrgStatusDictionary[itAccreditationResponse.organizationStatus || 'UNKNOWN']}
            </div>
          </div>
        </>
      )}
      {passportData?.pmErknm.subject && passportData?.pmErknm.subject.okveds && (
        <>
          <span className={styles.SectionTitle}>ОКВЭД</span>
          {passportData?.pmErknm.subject.okveds.map((item, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>ОКВЭД №{index + 1}</span>
              {item.name && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Наименование</div>
                  <div className={styles.ColValue}>{item.name}</div>
                </div>
              )}
            </div>
          ))}
        </>
      )}
      {passportData?.pmErknm.objects && passportData?.pmErknm.objects.length > 0 && (
        <KnmSubtitle title="Сведения об объектах контроля">
          {passportData?.pmErknm.objects.map((item, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>Объект №{index + 1}</span>
              {item.cosId && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Номер объекта</div>
                  <div
                    className={cn(styles.ColValue, { [styles.ColValueLink]: !!item.cosId })}
                    onClick={() => item.cosId && openErvkGovRu(item.cosId as string)}
                  >
                    {item.cosId}
                  </div>
                </div>
              )}
              <div className={styles.Row}>
                <div className={styles.ColText}>Иное указание места (нет в ФИАС)</div>
                <div className={styles.ColValue}>
                  <Checkbox checked={item.notInFIAS} size="medium" type="checkbox" theme="system" disabled={true} />
                </div>
              </div>
              {item.address && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Местонахождение</div>
                  <div className={styles.ColValue}>{item.address}</div>
                </div>
              )}
              {item.manualAddress && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Местонахождение</div>
                  <div className={styles.ColValue}>{item.manualAddress}</div>
                </div>
              )}
              {(item.region || item.fiasRegion) && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Субъект РФ</div>
                  <div className={styles.ColValue}>
                    {item.region || (typeof item.fiasRegion === 'object' ? item.fiasRegion.name : item.fiasRegion)}
                  </div>
                </div>
              )}
              {item.objectType && item.objectType.value && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Тип объекта</div>
                  <div className={styles.ColValue}>
                    {parseErknmValue<{ title?: string }>(item.objectType.value)?.title}
                  </div>
                </div>
              )}
              {item.objectKind && item.objectKind.value && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Вид объекта</div>
                  <div className={styles.ColValue}>
                    {parseErknmValue<{ title?: string }>(item.objectKind.value)?.title}
                  </div>
                </div>
              )}
              {item.objectSubKind && item.objectSubKind.value && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Подвид объекта</div>
                  <div className={styles.ColValue}>
                    {parseErknmValue<{ title?: string }>(item.objectSubKind.value)?.title}
                  </div>
                </div>
              )}
              {item.riskCategory && item.riskCategory.value && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Категория риска</div>
                  <div className={styles.ColValue}>
                    {parseErknmValue<{ title?: string }>(item.riskCategory.value)?.title}
                  </div>
                </div>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
      {passportData?.pmErknm?.inspectors && passportData?.pmErknm?.inspectors.length > 0 && (
        <KnmSubtitle title="Сведения о должностных лицах контрольных (надзорных) органов, участвующих в соответствующих профилактических мероприятиях">
          {passportData?.pmErknm?.inspectors.map((item, index) => {
            const positionTitle = item?.position?.value
              ? parseErknmValue<{ title?: string }>(item?.position?.value)
              : undefined;
            return (
              <div key={index}>
                <span className={styles.SectionTitle}>Должностное лицо №{index + 1}</span>
                {item.fullName && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Наименование</div>
                    <div className={styles.ColValue}>{item.fullName}</div>
                  </div>
                )}
                {item.position && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Должность</div>
                    <div className={styles.ColValue}>{positionTitle?.title}</div>
                  </div>
                )}
              </div>
            );
          })}
        </KnmSubtitle>
      )}
      {passportData?.pmErknm?.experts && passportData?.pmErknm?.experts.length > 0 && (
        <KnmSubtitle title="Сведения об экспертах, экспертных организациях, специалистах, независимых органах инспекции, саморегулируемых организациях и иных лицах, привлекаемых для проведения ПМ">
          {passportData?.pmErknm?.experts.map((item, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>Должностное лицо №{index + 1}</span>
              {item.title && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Наименование</div>
                  <div className={styles.ColValue}>{item.title}</div>
                </div>
              )}
              {item.type && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Тип</div>
                  <div className={styles.ColValue}>{item.type.name}</div>
                </div>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
      <KnmSubtitle title="Проведение мероприятия в форме совместного (межведомственного) контрольного (надзорного) мероприятия">
        <div className={styles.Row}>
          <div className={styles.ColText}>Проводится совместно</div>
          <div className={styles.ColValue}>{passportData?.pmErknm.isHasCollaboratingOrganization ? 'Да' : 'Нет'}</div>
        </div>
        {passportData?.pmErknm.collaboratingOrganizations &&
          passportData?.pmErknm.collaboratingOrganizations.length > 0 && (
            <>
              <span className={styles.SectionTitle}>
                Наименование органов контроля , с которым КНМ проводится совместно
              </span>
              {passportData?.pmErknm.collaboratingOrganizations.map((item, index) => (
                <div key={index}>
                  <span className={styles.SectionTitle}>Орган контроля №{index + 1}</span>
                  {item.organization && item.organization.value && (
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Наименование</div>
                      <div className={styles.ColValue}>
                        {parseErknmValue<{ fullName?: string }>(item.organization.value)?.fullName}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
      </KnmSubtitle>
      {passportData?.pmErknm.events != null && (
        <KnmSubtitle title="Перечень действий, осуществляемых в рамках ПМ">
          {passportData?.pmErknm.events.map((item, index) => (
            <div key={index} className={styles.PmEventContainer}>
              <span className={styles.SectionTitle}>Действие №{index + 1}</span>
              {item.event && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Действие</div>
                  <div className={styles.ColValue}>{parseErknmValue<{ title?: string }>(item.event.value)?.title}</div>
                </div>
              )}
              {item.interactionCategory && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Категория взаимодействия</div>
                  <div className={styles.ColValue}>{item.interactionCategory.name}</div>
                </div>
              )}
              {item.startDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата начала действия</div>
                  <div className={styles.ColValue}>
                    {formatDate({
                      date: item.startDate,
                      dateFormat: DATE_FORMAT_LONG,
                    })}
                  </div>
                </div>
              )}
              {item.stopDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата окончания действия</div>
                  <div className={styles.ColValue}>
                    {formatDate({
                      date: item.stopDate,
                      dateFormat: DATE_FORMAT_LONG,
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
    </div>
  );
};
